import {
  ADDRESS_PATH,
  CEILING_REINFORCEMENT_PATH,
  COMPLEMENT_PATH,
  CONFIRMATION_PATH,
  CONTRACT_VALIDATION_PATH,
  CROSS_SELLING_C_PATH,
  CROSS_SELLING_W_PATH,
  DEPTHS_PATH,
  DIRECT_DEBIT_PATH,
  DURABLE_DEVELOPMENT_PATH,
  HOUSING_RESIDENT_PATH,
  HOUSING_TENANT_PATH,
  HOUSING_TYPE_PATH,
  HOUSING_USE_PATH,
  INSURED_VALUE_PATH,
  INSURER_RESILIATION_PATH,
  LESSOR_NOTIFICATION_PATH,
  LESSOR_PATH,
  NUMBER_PIECES_PATH,
  OFFER_CUSTOMIZATION_PATH,
  OPTIONS_PATH,
  PERSONALIZATION_PATH,
  PREFERENTIAL_RATE_PATH,
  QUOTE_CONFIRMATION_PATH,
  REVENUS_PATH,
  ROOMMATES_INFO_PATH,
  SCHOOL_INSURANCE_PATH,
  SINISTER_HISTORY_PATH,
  SPECIAL_CONDITIONS_PATH,
  STUDENT_AGE_PATH,
  SURFACE_DEPENDANCES_PATH,
  SWIMMING_POOL_PATH,
} from 'src/app/shared/constants/route.constants';
import { GroupStep } from 'src/app/shared/models/model/concretes/components/group-step.model';
import { LayoutInfo } from 'src/app/shared/models/model/concretes/components/layout-info.model';
import { Step } from 'src/app/shared/models/model/concretes/components/step.model';
import { Subscription } from 'src/app/shared/models/model/concretes/shared/subcription.model';
import { isPartner } from 'src/app/shared/utils/brand-utils';
import { environment } from 'src/environments/environment';

/**
 * Options nécessaires pour construire l'arborescence des steps du parcours de souscription habitation.
 */
export interface HousingSubscriptionOptions {
  /** `true` lorsque le parcours est en offre "Essentielle", y compris depuis un comparateur. */
  isEssentielle: boolean;
  /** `true` lorsque la souscription courante possède des avenants. */
  hasAvenants: boolean;
}

/**
 * Construit la liste des `GroupStep` du parcours de souscription habitation.
 * Centralise la définition des steps afin qu'elle puisse être réutilisée
 * (composant de souscription, guards, calcul d'index par route, etc.)
 * sans dupliquer la structure du parcours.
 */
export function createHousingSubscriptionGroupSteps(options: HousingSubscriptionOptions): GroupStep[] {
  const { isEssentielle, hasAvenants } = options;

  return [
    new GroupStep([
      new Step(
        new LayoutInfo(1, 'Votre logement', 'Adresse', 'Adresse'),
        ADDRESS_PATH,
        true,
        true, // Appears by default in the breadcrumb
        false,
        true,
      ),
      new Step(
        new LayoutInfo(1, 'Votre logement', 'Usage du logement', 'Usage du logement'),
        HOUSING_USE_PATH,
        true,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(1, 'Votre logement', 'Maison/Appartement', 'Maison/Appartement'),
        HOUSING_TYPE_PATH,
        true,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(1, 'Votre logement', 'Locataire/Propriétaire', 'Locataire/Propriétaire'),
        HOUSING_TENANT_PATH,
        true,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(1, 'Votre logement', 'Habitants', 'Habitants'),
        HOUSING_RESIDENT_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(new LayoutInfo(1, 'Votre logement', 'Bailleur', 'Bailleur'), LESSOR_PATH, false, false, false, true),
      new Step(
        new LayoutInfo(1, 'Votre logement', 'Âge du souscripteur', 'Âge du souscripteur'),
        STUDENT_AGE_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(1, 'Votre logement', 'Antécédents sinistres', 'Antécédents sinistres'),
        SINISTER_HISTORY_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(1, 'Votre logement', 'Surface des dépendances', 'Surface des dépendances'),
        SURFACE_DEPENDANCES_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(new LayoutInfo(1, 'Votre logement', 'Revenus', 'Revenus'), REVENUS_PATH, false, false, false, true),
      new Step(
        new LayoutInfo(1, 'Votre logement', 'Nombre de pièces', 'Nombre de pièces'),
        NUMBER_PIECES_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(1, 'Votre logement', 'Dépendances', 'Dépendances'),
        DEPTHS_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(1, 'Votre logement', 'Installations dév. durable', 'Installations développement durable'),
        DURABLE_DEVELOPMENT_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(1, 'Votre logement', 'Piscine', 'Piscine'),
        SWIMMING_POOL_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(1, 'Votre logement', 'Valeur assurée', 'Valeur assurée'),
        INSURED_VALUE_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(new LayoutInfo(2, 'Vous contacter', 'Souscripteur'), 'subscriber', true, false, false, true),
      new Step(new LayoutInfo(3, 'Votre offre', 'Tarif'), 'offer-customize', true, false, false, true),
      new Step(
        new LayoutInfo(3, 'Votre offre', 'Renforcement plafond', 'Renforcement plafond'),
        CEILING_REINFORCEMENT_PATH,
        true,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(isEssentielle ? 1 : 3, 'Votre offre', 'Compléments', 'Compléments'),
        COMPLEMENT_PATH,
        true,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(3, 'Votre logement', 'Tarif préférentiel', 'Tarif préférentiel'),
        PREFERENTIAL_RATE_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(new LayoutInfo(3, 'Votre offre', 'Options', 'Options'), OPTIONS_PATH, true, false, false, true),
      new Step(
        new LayoutInfo(1, isEssentielle ? 'Votre offre' : 'Votre logement', 'Personnalisation', 'Personnalisation'),
        PERSONALIZATION_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(3, 'Votre offre', 'Assurance scolaire', 'Assurance scolaire'),
        SCHOOL_INSURANCE_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(4, 'Votre devis', 'Confirmation Devis'),
        QUOTE_CONFIRMATION_PATH,
        true,
        false,
        false,
        true,
      ),
    ]),
    new GroupStep([
      new Step(
        new LayoutInfo(
          1,
          'Votre Devis',
          isPartner(environment.brand) ? 'Accompagnement du client' : 'Questions additionnelles',
        ),
        CROSS_SELLING_C_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(2, 'Votre contrat', 'Services associés'),
        OFFER_CUSTOMIZATION_PATH,
        true,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(2, 'Votre contrat', 'Attestation bailleur'),
        LESSOR_NOTIFICATION_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(2, 'Votre contrat', 'Résiliation concurrence'),
        INSURER_RESILIATION_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(new LayoutInfo(2, 'Votre contrat', 'Colocataires'), ROOMMATES_INFO_PATH, false, false, false, true),
      new Step(new LayoutInfo(1, 'Votre Contrat', 'Prélèvement'), DIRECT_DEBIT_PATH, true, false, false, true),
      new Step(
        new LayoutInfo(2, 'Signature', hasAvenants ? 'Mise en place de votre avenant' : 'Vos documents contractuels'),
        SPECIAL_CONDITIONS_PATH,
        true,
        false,
        false,
        true,
      ),
      new Step(new LayoutInfo(2, 'Signature', 'Code de Confirmation'), CONFIRMATION_PATH, true, false, false, true),
      new Step(
        new LayoutInfo(3, 'Votre offre', 'Questions additionnelles'),
        CROSS_SELLING_W_PATH,
        false,
        false,
        false,
        true,
      ),
      new Step(
        new LayoutInfo(3, 'Votre contrat', 'Validation du contrat'),
        CONTRACT_VALIDATION_PATH,
        true,
        false,
        false,
        true,
      ),
    ]),
  ];
}

/**
 * Construit la `Subscription` complète du parcours habitation.
 */
export function createHousingSubscription(options: HousingSubscriptionOptions): Subscription {
  return new Subscription(createHousingSubscriptionGroupSteps(options));
}

/**
 * Retourne l'index (groupIndex / stepIndex) de la step correspondant à une route donnée,
 * en se basant sur la structure de référence du parcours de souscription habitation.
 *
 * Permet une redirection robuste (ex: partnerGuard) sans dépendre d'index en dur qui
 * deviennent invalides dès que l'ordre des steps change.
 */
export function findHousingSubscriptionStepIndex(
  url: string,
  options: HousingSubscriptionOptions = { isEssentielle: false, hasAvenants: false },
): { groupIndex: number; stepIndex: number } | null {
  return createHousingSubscription(options).findStepIndexesByUrl(url);
}
